function skuAlnum(value) {
  return String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function skuCodePart(value, length = 3) {
  const cleaned = skuAlnum(value);
  if (cleaned.length >= length) return cleaned.slice(0, length);
  return `${cleaned}${'X'.repeat(length)}`.slice(0, length);
}

function skuShortHash(value) {
  const text = String(value || '');
  let hash = 5381;
  for (let i = 0; i < text.length; i += 1) {
    hash = ((hash << 5) + hash) ^ text.charCodeAt(i);
  }
  return (hash >>> 0).toString(16).toUpperCase().padStart(4, '0').slice(-4);
}

export function buildSkuBase(row, categories) {
  const cat = (categories || []).find((c) => c.id === row.category);
  const catCode = skuCodePart(cat?.name || 'GEN', 3);
  const nameCode = skuCodePart(row.name || row.brand || 'PRD', 3);
  const barcodeDigits = String(row.barcode || '').replace(/\D/g, '');
  const uniquePart = barcodeDigits.length >= 4
    ? barcodeDigits.slice(-4)
    : skuShortHash(`${row.name}|${row.brand}|${barcodeDigits}|${row.id || ''}`);
  return `${catCode}-${nameCode}-${uniquePart}`;
}

export function allocateUniqueSku(base, used) {
  let sku = base;
  let n = 2;
  while (used.has(sku.toUpperCase())) {
    sku = `${base}-${n}`;
    n += 1;
  }
  used.add(sku.toUpperCase());
  return sku;
}

export function canAutoSku(row) {
  return Boolean(
    String(row.name || '').trim()
    && String(row.category || '').trim()
    && String(row.barcode || '').trim(),
  );
}

/**
 * Apply auto SKUs for a list of rows:
 * - Never overwrites manually entered SKUs (skuAuto !== true)
 * - Regenerates auto SKUs when name / category / barcode change
 * - Only generates when name, category, and barcode are all present
 */
export function applyAutoSkus(rows, products, categories) {
  const used = new Set(
    (products || [])
      .map((p) => String(p.sku || '').trim().toUpperCase())
      .filter(Boolean),
  );

  rows.forEach((row) => {
    const sku = String(row.sku || '').trim().toUpperCase();
    if (sku && !row.skuAuto) used.add(sku);
  });

  return rows.map((row) => {
    const hasSku = Boolean(String(row.sku || '').trim());
    if (hasSku && !row.skuAuto) return row;

    if (!canAutoSku(row)) {
      if (!hasSku && !row.skuAuto) return row;
      return { ...row, sku: '', skuAuto: false };
    }

    const base = buildSkuBase(row, categories);
    return { ...row, sku: allocateUniqueSku(base, used), skuAuto: true };
  });
}

/** Generate one SKU for a single product form, avoiding collisions with catalog products. */
export function generateProductSku(formRow, products, categories) {
  if (!canAutoSku(formRow)) return '';
  const used = new Set(
    (products || [])
      .map((p) => String(p.sku || '').trim().toUpperCase())
      .filter(Boolean),
  );
  const base = buildSkuBase(formRow, categories);
  return allocateUniqueSku(base, used);
}
